import React, { useState } from 'react';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email) return;

    setIsLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('email', email);

      const response = await fetch('/!/forms/newsletter', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      if (response.status === 419) {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
        const retryResponse = await fetch('/!/forms/newsletter', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfToken || '',
          },
          body: formData,
        });

        return handleResponse(retryResponse);
      }

      handleResponse(response);
    } catch (err) {
      console.error('Network error:', err);
      setError('Network error. Please check your connection and try again.');
      setIsLoading(false);
    }
  };

  const handleResponse = async (response) => {
    try {
      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubscribed(true);
        setEmail('');
      } else {
        setError(result.error || `Subscription failed (Status: ${response.status}). Please try again.`);
      }
    } catch (parseError) {
      console.error('Error parsing response:', parseError);
      setError('Invalid response from server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookAppointment = () => {
    window.location.href = '/book-appointment-guided-care-journey';
  };

  if (isSubscribed) {
    return (
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto">
            <div className="p-12 rounded-3xl bg-white shadow-xl border border-gray-200">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <Icon name="CheckCircle" size={40} className="text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-3 text-center">
                Subscription Successful!
              </h2>
              <p className="text-gray-600 mb-8 text-center">
                Thank you for subscribing to our newsletter. You'll receive the latest 
                eye health tips, treatment updates, and resources directly in your inbox.
              </p>
              <div className="flex justify-center">
                <Button
                  size="lg"
                  onClick={handleBookAppointment}
                  className="shadow-md hover:shadow-lg transition-all"
                >
                  Book an Appointment
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="p-12 rounded-3xl bg-white shadow-xl border border-gray-200">
            <div className="grid md:grid-cols-2 gap-10 items-center">

              {/* Info Column */}
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mr-4">
                    <Icon name="Mail" size={26} className="text-[#08005f]" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-800">
                    Stay Informed
                  </h2>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  Subscribe to our newsletter for the latest eye health tips, 
                  treatment updates, and expert wellness advice from our team.
                </p>

                <ul className="space-y-3">
                  {[
                    'Monthly eye health tips and advice',
                    'Latest treatment options and technologies',
                    'Seasonal eye care reminders',
                    'Exclusive patient resources and guides'
                  ]?.map((benefit, index) => (
                    <li key={index} className="flex items-center">
                      <Icon name="Check" size={18} className="text-green-600 mr-3" />
                      <span className="text-gray-800 text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Form Column */}
              <div>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => {
                        setEmail(e?.target?.value);
                        setError('');
                      }}
                      required
                      className="focus:ring-2 focus:ring-blue-200 transition"
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-600 text-sm flex items-center">
                        <Icon name="AlertCircle" size={16} className="mr-2" />
                        {error}
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    disabled={isLoading || !email}
                    className="shadow-md hover:shadow-lg transition-all"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Subscribing...
                      </div>
                    ) : (
                      <>
                        <Icon name="Mail" size={16} className="mr-2" />
                        Subscribe to Newsletter
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-gray-500 mt-4 leading-relaxed">
                  By subscribing, you agree to receive email communications from Eye&U Kenya. 
                  You can unsubscribe at any time.
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;
